import { queryOptions } from '@tanstack/react-query';

import { api } from '@/lib/api/api';
import type {
  Permission,
  PermissionSet,
  PermissionSetComposition,
  PermissionSetCompositionAsOf,
  RegisteredFeature,
} from '@/lib/api/domains/permissions';
import { keys } from '@/lib/query-keys';

/*
 * The permission catalog read (F03 R41). How the UI CONSUMES the
 * `GET /permissions` transport: the queryOptions the catalog screen reads. The
 * server relays Identity's catalog unchanged; the frontend renders exactly
 * those rows as record-kind + action with the description where present
 * (AC-51f2752f, AC-3ce30755) and never composes a catalog entry of its own.
 *
 * Keyed by the catalog key so a successful define (mutate → invalidate)
 * refreshes exactly the catalog.
 */
export function permissionCatalogQuery() {
  return queryOptions<Permission[]>({
    queryKey: keys.permissions.catalog(),
    queryFn: () => api.permissions.listCatalog(),
  });
}

/*
 * The registered-features read (F03 R41, AC-d8754ca3). How the UI consumes the
 * `GET /permissions/features` transport: the queryOptions the define-permission
 * surface reads to offer the record-kind. The record-kind must be a feature
 * already registered with Identity (AC-d8754ca3); the surface offers only these
 * so it offers only what Identity would accept, with Identity's FK check the
 * backstop. The action, by contrast, is free text and comes from no list.
 */
export function registeredFeaturesQuery() {
  return queryOptions<RegisteredFeature[]>({
    queryKey: keys.permissions.features(),
    queryFn: () => api.permissions.listRegisteredFeatures(),
  });
}

/*
 * The permission-sets read (F03 R46, R52). How the UI consumes the
 * `GET /permission-sets` transport: the queryOptions the composer and the set
 * list read. The server relays Identity's sets unchanged; the frontend renders
 * exactly those sets and composes none of its own.
 */
export function permissionSetsQuery() {
  return queryOptions<PermissionSet[]>({
    queryKey: keys.permissionSets.lists(),
    queryFn: () => api.permissions.listSets(),
  });
}

/*
 * One set's current composition (F03 R49, AC-cf93b725). How the UI consumes the
 * `GET /permission-sets/:id` transport: the queryOptions the composer reads
 * when it opens for an EXISTING set, so it pre-selects exactly the permissions
 * the set currently contains. Keyed per set so opening the composer re-reads
 * the authoritative current membership and a successful replace invalidates
 * exactly that set's composition. An empty composition is a set that grants
 * nothing (R51) — a valid read, never treated as missing.
 */
export function permissionSetCompositionQuery(setId: string) {
  return queryOptions<PermissionSetComposition>({
    queryKey: keys.permissionSets.composition(setId),
    queryFn: () => api.permissions.readSetComposition(setId),
  });
}

/*
 * What a set contained AS OF A STATED EARLIER DATE (F03 R48, AC-b8ffb235; the
 * tpa half of the rollup AC-779bdd64). How the UI consumes the
 * `GET /permission-sets/:id/composition?asOf=…` transport: the queryOptions the
 * F69 history surface reads to show what the set held on a chosen earlier date.
 * Keyed per set AND per date so each stated date is cached and read
 * independently of the current composition and of other dates. An empty
 * `permissionIds` is a set that contained nothing on that date — a valid read.
 *
 * Built on the CR-E01-15 ASSUMPTION (confirm bucket — DEPENDS, not BLOCKED):
 * Identity records set-membership before/after each change and returns a set's
 * composition as of a stated earlier date. See
 * `lib/api/domains/permissions` `PermissionSetCompositionAsOf` for the full
 * assumption note and what to re-check if CR-E01-15 is not confirmed as
 * assumed. This is a read — F03 records; F03b refuses — never an authorization
 * decision.
 */
export function permissionSetCompositionAsOfQuery(setId: string, asOf: string) {
  return queryOptions<PermissionSetCompositionAsOf>({
    queryKey: keys.permissionSets.compositionAsOf(setId, asOf),
    queryFn: () => api.permissions.readSetCompositionAsOf(setId, asOf),
  });
}
